/**
 * Request schemas shared by the API (authoritative validation) and the web forms.
 * Numeric inputs accept numbers or strings because the original coerces form strings
 * with `Number(x) || default`; the business rules apply those exact coercions.
 */
import { z } from 'zod';
import { BEHAVIOR_FLAG_IDS } from '../constants/behavior-flags';
import { ORDER_STATUSES, OPPORTUNITY_STAGES } from '../constants';

const str = (max = 500) => z.string().max(max);
const optStr = (max = 500) => z.string().max(max).optional().default('');
export const numLike = z.union([z.number(), z.string().max(40)]).optional();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'تاريخ غير صالح (YYYY-MM-DD)');
const optDate = z.union([isoDate, z.literal('')]).optional().default('');
const id = z.string().min(1).max(64);
const payMethod = z.enum(['cash', 'card', 'transfer']);

export const listQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(1000).default(100),
  q: z.string().max(200).optional().default(''),
  sort: z.string().max(60).optional(),
});
export type ListQuery = z.infer<typeof listQuerySchema>;

// ─── Auth & users ───────────────────────────────────────────────────────────
export const loginSchema = z.object({ email: z.string().email().max(200), password: z.string().min(1).max(200) });
export const passwordPolicy = z
  .string()
  .min(10, 'كلمة المرور يجب ألا تقل عن 10 أحرف')
  .max(200)
  .refine((p) => /[A-Za-z]/.test(p) && /\d/.test(p), 'يجب أن تحتوي على حروف وأرقام');
export const changePasswordSchema = z.object({ currentPassword: z.string().min(1).max(200), newPassword: passwordPolicy });
export const userCreateSchema = z.object({
  email: z.string().email().max(200),
  name: str(120).min(1),
  roleKey: str(40).min(1),
  password: passwordPolicy,
});
export const userUpdateSchema = z.object({
  name: str(120).min(1).optional(),
  roleKey: str(40).min(1).optional(),
  isActive: z.boolean().optional(),
  password: passwordPolicy.optional(),
});
export const roleUpdateSchema = z.object({
  name: str(120).min(1).optional(),
  description: optStr(300),
  permissions: z.array(str(60)).max(200),
});
export const passwordResetRequestSchema = z.object({ email: z.string().email().max(200) });
export const passwordResetConfirmSchema = z.object({ token: str(200).min(10), newPassword: passwordPolicy });

// ─── Customers ──────────────────────────────────────────────────────────────
export const customerSchema = z.object({
  name: str(200),
  type: optStr(60),
  segment: optStr(60),
  city: optStr(120),
  phone: optStr(60),
  email: optStr(200),
  company: optStr(200),
  notes: optStr(4000),
});

// ─── Opportunities ──────────────────────────────────────────────────────────
export const opportunitySchema = z.object({
  title: str(300),
  customer: optStr(200),
  value: numLike,
  stage: z.enum(OPPORTUNITY_STAGES).default('lead'),
  probability: numLike,
  next: optStr(500),
  date: optDate,
});
export const opportunityStageSchema = z.object({ stage: z.enum(OPPORTUNITY_STAGES) });

// ─── Orders & payments ──────────────────────────────────────────────────────
export const orderItemSchema = z.object({
  productId: optStr(64),
  name: optStr(300),
  nameEn: optStr(300),
  code: optStr(100),
  contents: optStr(2000),
  size: optStr(200),
  image: optStr(500),
  qty: numLike,
  price: numLike,
});
export const orderSaveSchema = z.object({
  num: str(60),
  customerId: optStr(64),
  customerName: str(200),
  customerPhone: optStr(60),
  employeeName: optStr(200),
  employeeNameCustom: optStr(200),
  status: z.enum(ORDER_STATUSES).default('received'),
  occasion: optStr(300),
  venue: optStr(300),
  deliveryDate: optDate,
  deliveryTime: optStr(40),
  deliveryAddress: optStr(500),
  deliveryMethod: z.enum(['delivery', 'pickup']).default('delivery'),
  payMethod: payMethod.default('cash'),
  initialPay: numLike,
  trayDeposit: numLike,
  hasSupervisor: z.boolean().default(false),
  notes: optStr(4000),
  items: z.array(orderItemSchema).max(300),
  acknowledgeWarnings: z.boolean().optional().default(false),
});
export type OrderSaveInput = z.infer<typeof orderSaveSchema>;
export const orderValidateSchema = orderSaveSchema.extend({ editId: z.string().max(64).optional() });
export const orderStatusSchema = z.object({ status: z.enum(ORDER_STATUSES) });
export const checklistSchema = z.object({ checks: z.array(z.boolean()).max(20) });
export const paymentCreateSchema = z.object({ amount: numLike, method: payMethod });
export const paymentEditSchema = z.object({ amount: numLike, method: payMethod });

// ─── Products ───────────────────────────────────────────────────────────────
export const productSchema = z.object({
  code: optStr(100),
  name: str(300),
  nameEn: optStr(300),
  size: optStr(200),
  contents: optStr(4000),
  price: numLike,
  cost: numLike,
  cat: optStr(120),
  section: optStr(40),
  image: optStr(500),
  stockQty: numLike,
  supplierId: optStr(64),
  minStock: numLike,
});

// ─── Quotes ─────────────────────────────────────────────────────────────────
export const quoteItemSchema = z.object({
  productId: optStr(64),
  name: optStr(300),
  nameEn: optStr(300),
  code: optStr(100),
  size: optStr(200),
  contents: optStr(2000),
  image: optStr(500),
  qty: numLike,
  cost: numLike,
});
export const quoteSchema = z.object({
  name: str(300),
  customer: optStr(200),
  phone: optStr(60),
  notes: optStr(2000),
  margin: numLike,
  items: z.array(quoteItemSchema).max(300),
});

// ─── POS ────────────────────────────────────────────────────────────────────
export const posSaleSchema = z.object({
  cart: z
    .array(
      z.object({
        productId: id,
        name: optStr(300),
        nameEn: optStr(300),
        code: optStr(100),
        image: optStr(500),
        price: z.number(),
        qty: z.union([z.number(), z.string().max(40)]),
      }),
    )
    .max(300),
  discount: numLike,
  discountType: z.enum(['amount', 'percent']).default('amount'),
  paid: numLike,
  payMethod: payMethod,
  customerName: optStr(200),
  customerPhone: optStr(60),
  note: optStr(1000),
  terminal: optStr(60),
  cashier: optStr(200),
});

// ─── Suppliers & purchasing ─────────────────────────────────────────────────
export const supplierSchema = z.object({ name: str(200), phone: optStr(60), city: optStr(120), notes: optStr(2000) });
export const purchaseSchema = z.object({
  supplierId: optStr(64),
  num: optStr(60),
  date: optDate,
  notes: optStr(2000),
  items: z
    .array(z.object({ productId: optStr(64), name: optStr(300), qty: numLike, cost: numLike }))
    .max(300),
});
export const supplierPaymentSchema = z.object({
  supplierId: optStr(64),
  amount: numLike,
  method: payMethod.default('cash'),
  date: optDate,
  notes: optStr(1000),
});

// ─── Inventory ──────────────────────────────────────────────────────────────
const countsRecord = z.record(z.string().max(64), z.union([z.number(), z.string().max(40)]));
export const stocktakeSchema = z.object({ counts: countsRecord, note: optStr(500) });
export const trayReturnSchema = z.object({ counts: countsRecord, note: optStr(500) });
export const stockAdjustSchema = z.object({ qty: numLike, reason: optStr(120) });
export const lostTraySchema = z.object({
  productId: optStr(64),
  qty: numLike,
  reason: optStr(200),
  orderNum: optStr(60),
  notes: optStr(1000),
});

// ─── Accounting & settings ──────────────────────────────────────────────────
export const manualJournalSchema = z.object({
  date: optDate,
  ref: optStr(100),
  label: optStr(300),
  debitAcc: str(10),
  creditAcc: str(10),
  amount: numLike,
  note: optStr(1000),
});
export const companySettingsSchema = z.object({
  companyName: optStr(200),
  companyNameEn: optStr(200),
  crNumber: optStr(60),
  vatNumber: optStr(60),
  companyAddress: optStr(300),
  companyCity: optStr(120),
  companyPhone: optStr(60),
  companyEmail: optStr(200),
  vatRate: numLike,
  pricesIncludeVat: z.boolean().default(false),
});
export const targetSchema = z.object({ monthlyTarget: numLike });
export const generalSettingsSchema = z.object({
  fiscalYearStart: z.string().regex(/^\d{2}-\d{2}$/).optional(),
  invoicePrefix: str(20).optional(),
  posPrefix: str(20).optional(),
  posTerminal: str(60).optional(),
  companyEmail: str(200).optional(),
});
export const behaviorFlagsSchema = z.object(
  Object.fromEntries(BEHAVIOR_FLAG_IDS.map((k) => [k, z.boolean().optional()])) as Record<
    (typeof BEHAVIOR_FLAG_IDS)[number],
    z.ZodOptional<z.ZodBoolean>
  >,
);

// ─── Team, attendance, dormant modules ──────────────────────────────────────
export const teamSchema = z.object({ name: str(200), role: optStr(120), phone: optStr(60), kpi: optStr(300), score: numLike });
export const attendanceSchema = z.object({
  date: optDate,
  employeeId: optStr(64),
  employeeName: optStr(200),
  type: z.enum(['work', 'ot', 'leave', 'absent', 'permission', 'remote']).default('work'),
  checkIn: optStr(10),
  checkOut: optStr(10),
  hours: numLike,
  otHours: numLike,
  note: optStr(1000),
});
export const appointmentSchema = z.object({
  customerName: str(200),
  customerPhone: optStr(60),
  date: optDate,
  time: optStr(10),
  place: z.enum(['branch', 'home']).default('branch'),
  address: optStr(500),
  purpose: optStr(500),
  notes: optStr(2000),
  status: z.enum(['scheduled', 'done', 'cancelled', 'no_show']).default('scheduled'),
});
export const appointmentStatusSchema = z.object({ status: z.enum(['scheduled', 'done', 'cancelled', 'no_show']) });
export const seasonalSchema = z.object({
  name: str(200),
  start: optDate,
  end: optDate,
  target: numLike,
  notes: optStr(4000),
  goals: optStr(4000),
});
export const complaintSchema = z.object({
  num: optStr(60),
  customer: optStr(200),
  desc: str(4000),
  root: optStr(1000),
  action: optStr(1000),
  status: z.enum(['open', 'investigating', 'resolved', 'closed']).default('open'),
});

export const aiAskSchema = z.object({ question: z.string().max(1000) });
export const resetDataSchema = z.object({ confirm: z.literal('مسح كل البيانات'), password: z.string().min(1).max(200) });
